import React, { useState } from 'react';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import {
  CircularProgress,
  IconButton,
  IconButtonProps,
  Tooltip,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { authService } from '../../services/AuthService';
import { chatInboxFromPath } from '../../utils/chat';
import { openOrCreateChatWith } from '../../utils/chatPartners';

export interface ChatWithUserIconButtonProps {
  partnerUserId: string;
  /** Used in tooltip / aria-label. */
  partnerName?: string | null;
  /** Chat list base path for the current role, e.g. `/tutor/chats`. */
  basePath: '/user/chats' | '/tutor/chats' | '/host/chats';
  size?: IconButtonProps['size'];
  disabled?: boolean;
  sx?: IconButtonProps['sx'];
}

/**
 * Opens an existing 1:1 chat with `partnerUserId`, or creates one, then navigates to it.
 * Renders disabled when the partner is the current user (self-bookings in local test).
 */
const ChatWithUserIconButton: React.FC<ChatWithUserIconButtonProps> = ({
  partnerUserId,
  partnerName,
  basePath,
  size = 'small',
  disabled = false,
  sx,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [opening, setOpening] = useState(false);
  const [lastError, setLastError] = useState(false);

  const trimmedPartnerId = partnerUserId.trim();
  const currentUserId = authService.getUserId() ?? '';
  const isSelf = !!currentUserId && currentUserId === trimmedPartnerId;
  const canOpen = !!trimmedPartnerId && !!currentUserId && !isSelf;

  const displayName = partnerName?.trim();
  const tooltip = !trimmedPartnerId
    ? t('chat.open.withUser', 'Open chat')
    : !currentUserId
      ? t('chat.open.profileRequired', 'Load your profile before starting a chat.')
      : isSelf
        ? t('chat.open.self', 'You can’t chat with yourself')
        : opening
          ? t('chat.open.opening', 'Opening chat…')
          : displayName
            ? t('chat.open.withName', 'Chat with {{name}}', { name: displayName })
            : t('chat.open.withUser', 'Open chat');
  const errorTooltip = t('chat.open.error', 'Could not open chat. Try again.');

  if (!trimmedPartnerId) {
    return null;
  }

  const handleClick = async (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (opening || !canOpen) return;

    setOpening(true);
    setLastError(false);
    try {
      const chatId = await openOrCreateChatWith(
        currentUserId,
        trimmedPartnerId,
        chatInboxFromPath(basePath)
      );
      navigate(`${basePath}/${chatId}`);
    } catch {
      setLastError(true);
    } finally {
      setOpening(false);
    }
  };

  return (
    <Tooltip title={lastError ? errorTooltip : tooltip} arrow>
      <span>
        <IconButton
          size={size}
          color="inherit"
          disabled={disabled || opening || !canOpen}
          aria-label={tooltip}
          onClick={handleClick}
          sx={{
            p: 0.25,
            ml: -0.25,
            color: lastError ? 'error.main' : 'text.secondary',
            '&:hover': canOpen
              ? { color: 'primary.main', bgcolor: 'action.hover' }
              : undefined,
            ...sx,
          }}
        >
          {opening ? (
            <CircularProgress size={12} color="inherit" />
          ) : (
            <ForumOutlinedIcon sx={{ fontSize: 15 }} />
          )}
        </IconButton>
      </span>
    </Tooltip>
  );
};

export default ChatWithUserIconButton;
